import React from 'react';
import styled from 'styled-components';
import { GridSection, Eyebrow, SectionTitle } from './GridSection';
import letterboxd from '../data/letterboxd.json';
import spotify from '../data/spotify.json';
import personal from '../data/personal.json';

const Block = styled.div`
  padding: 24px 0;
  border-top: 1px solid var(--rule);

  &:last-child { border-bottom: 1px solid var(--rule); }
`;

const Head = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const Label = styled.h3`
  font-family: 'EB Garamond', Georgia, serif;
  font-size: 1.2rem;
  font-weight: 500;
  color: var(--ink);
  margin: 0;
  letter-spacing: -0.005em;
`;

const HeadLink = styled.a`
  font-style: italic;
  font-size: 0.95rem;
  color: var(--ink-muted);
  text-decoration: none;
  border-bottom: 1px solid var(--rule);
  padding-bottom: 1px;

  &:hover { color: var(--accent); border-color: var(--accent); text-decoration: none; }
`;

const PosterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;

  @media (max-width: 720px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

const SquareGrid = styled(PosterGrid)``;

const PosterTile = styled.a`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  transition: transform .2s ease;

  &:hover { transform: translateY(-2px); text-decoration: none; }
`;

const PosterFrame = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: ${p => p.square ? '1 / 1' : '2 / 3'};
  border-radius: 3px;
  overflow: hidden;
  background: var(--paper-deep);
  border: 1px solid var(--rule);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    filter: saturate(0.92) contrast(0.97);
  }

  .meta {
    position: absolute;
    inset: auto 0 0 0;
    padding: 8px 9px;
    background: linear-gradient(transparent, rgba(20, 16, 12, 0.92));
    color: #f3ecdf;
    font-size: 0.78rem;
    line-height: 1.3;
    opacity: 0;
    transition: opacity .2s;
    font-family: 'EB Garamond', Georgia, serif;
  }
  ${PosterTile}:hover & .meta { opacity: 1; }
  .title { font-weight: 500; display: block; }
  .sub { color: rgba(243, 236, 223, 0.85); font-style: italic; }

  /* On touch devices the hover overlay never appears, so hide it. */
  @media (hover: none) and (pointer: coarse) {
    .meta { display: none; }
  }
`;

const Caption = styled.div`
  margin-top: 8px;
  font-family: 'EB Garamond', Georgia, serif;
  font-size: 0.88rem;
  line-height: 1.3;
  color: var(--ink-soft);
  text-align: center;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  .title { font-style: italic; }
  .sub { display: block; font-size: 0.78rem; color: var(--ink-muted); margin-top: 1px; }

  /* Hidden on desktop hover-capable devices (the hover overlay does the work). */
  @media (hover: hover) and (pointer: fine) and (min-width: 601px) {
    display: none;
  }
`;

function ratingStars(n) {
  if (n == null) return '';
  const full = '★'.repeat(Math.floor(n));
  const half = (n % 1 >= 0.5) ? '½' : '';
  return full + half;
}

function steamCover(appId) {
  return `https://shared.akamai.steamstatic.com/store_item_assets/steam/apps/${appId}/library_600x900.jpg`;
}

function steamPage(appId) {
  return `https://store.steampowered.com/app/${appId}/`;
}

function Personal() {
  const films = (letterboxd.films || []).slice(0, 8);
  const albums = (spotify.albums || []).slice(0, 8);
  const games = (personal.games && personal.games.recent) || [];
  const spotifyProfileUrl = (spotify && spotify.profileUrl) ||
    (personal.spotify && personal.spotify.profileUrl);

  return (
    <GridSection id="personal">
      <Eyebrow>vi — off the clock</Eyebrow>
      <SectionTitle>Off the clock.</SectionTitle>

      {albums.length > 0 && (
        <Block>
          <Head>
            <Label>Music</Label>
            {spotifyProfileUrl && (
              <HeadLink href={spotifyProfileUrl} target="_blank" rel="noopener noreferrer">spotify ↗</HeadLink>
            )}
          </Head>
          <SquareGrid>
            {albums.map(a => (
              <PosterTile key={a.id} href={a.url} target="_blank" rel="noopener noreferrer">
                <PosterFrame square>
                  {a.thumbnail && <img src={a.thumbnail} alt={a.title} loading="lazy" />}
                  <div className="meta">
                    <span className="title">{a.title}</span>
                  </div>
                </PosterFrame>
                <Caption>
                  <span className="title">{a.title}</span>
                </Caption>
              </PosterTile>
            ))}
          </SquareGrid>
        </Block>
      )}

      <Block>
        <Head>
          <Label>Films</Label>
          <HeadLink href={letterboxd.profileUrl} target="_blank" rel="noopener noreferrer">letterboxd ↗</HeadLink>
        </Head>
        <PosterGrid>
          {films.map(f => (
            <PosterTile key={f.link} href={f.link} target="_blank" rel="noopener noreferrer">
              <PosterFrame>
                {f.poster && <img src={f.poster} alt={f.title} loading="lazy" />}
                <div className="meta">
                  <span className="title">{f.title}</span>
                  <span className="sub">{ratingStars(f.rating)}</span>
                </div>
              </PosterFrame>
              <Caption>
                <span className="title">{f.title}</span>
                {f.rating != null && <span className="sub">{ratingStars(f.rating)}</span>}
              </Caption>
            </PosterTile>
          ))}
        </PosterGrid>
      </Block>

      <Block>
        <Head>
          <Label>Games</Label>
          {personal.games && personal.games.profileUrl && (
            <HeadLink href={personal.games.profileUrl} target="_blank" rel="noopener noreferrer">psn profiles ↗</HeadLink>
          )}
        </Head>
        <PosterGrid>
          {games.map(g => (
            <PosterTile
              key={g.title}
              href={g.steamAppId ? steamPage(g.steamAppId) : (personal.games.profileUrl || '#')}
              target="_blank"
              rel="noopener noreferrer"
            >
              <PosterFrame>
                {g.steamAppId && (
                  <img src={steamCover(g.steamAppId)} alt={g.title} loading="lazy" />
                )}
                <div className="meta">
                  <span className="title">{g.title}</span>
                  {g.year && <span className="sub">{g.year}</span>}
                </div>
              </PosterFrame>
              <Caption>
                <span className="title">{g.title}</span>
                {g.year && <span className="sub">{g.year}</span>}
              </Caption>
            </PosterTile>
          ))}
        </PosterGrid>
      </Block>
    </GridSection>
  );
}

export default Personal;
